import { Box, Typography, Button, useTheme } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { observer } from 'mobx-react'

import FacultyMaterialsList from '../../components/study-material-components/FacultyMaterialsList'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import { useStudyMaterialStore } from '../../store/studymaterial.store'
import { useOrganizationStore } from '../../store/organization.store'
import CustomSearchBar from '../../components/common/CustomSearchBar'
import { useUserStore } from '../../store/user.store'
import fileLogo from '../../assets/no-material.svg'
import { Icons } from '../../utils/utilities'


const FacultyMaterials = observer( () => {

    const [materials, setMaterials] = useState( [] )
    const [filteredMaterials, setFilteredMaterials] = useState( [] )
    const [isLoading, setIsLoading] = useState( true )
    const [rowsPerPage, setRowsPerPage] = useState( +( localStorage.getItem( 'StudyMaterialRowsPerPage' ) || 50 ) )
    const [page, setPage] = useState( 0 )
    const [isInstitutesLoading, setIsInstitutesLoading] = useState( false )
    const [instituteMenuAnchor, setInstituteMenuAnchor] = useState( null )

    const [searchParams, setSearchParams] = useSearchParams( { mode: "manage", searchText: "", institute: "0" } )
    const mode = searchParams.get( 'mode' )
    const institute = searchParams.get( 'institute' ) || "0"
    const searchText = searchParams.get( 'searchText' ) || ""

    const UserStore = useUserStore()
    const OrganizationStore = useOrganizationStore()

    const { palette, border, layout } = useTheme()

    const navigate = useNavigate()
    const StudyMaterialStore = useStudyMaterialStore()

    const searchMaterials = ( e ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( "searchText", e.target.value )
            return newParams
        }, { replace: true } )
        localStorage.setItem( "materialsSearchText", e.target.value )
    }

    const handleStudentInstituteChange = ( val ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( "institute", val )
            return newParams
        }, { replace: true } )
        setInstituteMenuAnchor( null )
        localStorage.setItem( "materialsSearchInstitute", val )
    }

    const handleChangePage = ( event, newPage ) => {
        setPage( newPage )
    }

    const removeFilters = async () => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( "institute", "0" )
            newParams.set( "searchText", "" )
            return newParams
        }, { replace: true } )
        if ( StudyMaterialStore.getErrors.materials )
            await StudyMaterialStore.fetchMaterials()
        setMaterials( StudyMaterialStore.getMaterials["materials"] )
    }

    const handleChangeRowsPerPage = ( event ) => {
        setRowsPerPage( parseInt( event.target.value, 10 ) )
        localStorage.setItem( 'StudyMaterialRowsPerPage', event.target.value )
        setPage( 0 )
    }

    useEffect( () => {
        const getData = async () => {
            localStorage.setItem( "studyMaterialMode", "manage" )
            if ( ( UserStore.getUser.user_role === 'FACULTY' || UserStore.getUser.user_role === 'HOD' || UserStore.getUser.user_role === 'PRINCIPAL' ) ) {
                if ( StudyMaterialStore.getErrors.materials )
                    await StudyMaterialStore.fetchMaterials()
                setMaterials( StudyMaterialStore.getMaterials["materials"] )
            } else {
                setIsInstitutesLoading( true )
                if ( OrganizationStore.getOrganization?.length === 0 )
                    await OrganizationStore.fetchOrganization()
                setIsInstitutesLoading( false )
                if ( StudyMaterialStore.getErrors.materials )
                    await StudyMaterialStore.fetchMaterials()
                setMaterials( StudyMaterialStore.getMaterials["materials"] )
            }
            setIsLoading( false )
        }
        getData()
    }, [UserStore, StudyMaterialStore, OrganizationStore] )

    useEffect( () => {
        const searchRegExp = new RegExp( searchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' )
        setFilteredMaterials(
            materials.filter( material => {
                return ( material.subject_name?.match( searchRegExp ) ||
                    material.study_material_name?.match( searchRegExp ) ||
                    material.subject_code?.match( searchRegExp ) ||
                    material.study_material_created_by?.match( searchRegExp ) ||
                    material?.study_material_tags?.match( searchRegExp )
                ) && ( material.institute === institute || institute === "0" )
            } )
        )
    }, [materials, searchText, institute] )

    return (
        <Box minWidth="0" overflow="auto" flexGrow={1} display="flex" flexDirection="column" padding={layout.pagePadding}>

            {/* Main Content */}
            <Box>
                <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Your Study Materials</Typography>
                <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Manage your material from here.</Typography>
            </Box>
            <Box display="flex" gap="10px" marginTop="20px" alignItems="center" >
                <CustomSearchBar style={{ flexGrow: 1, width: "400px", marginRight: "0" }} border={border[1]} value={searchText} onChange={searchMaterials} placeholder={`Search by Subjects,${UserStore.getUser.user_role === 'STUDENT' ? " Faculty," : ""} Title or Subject code...`} />
                {( UserStore.getUser.user_role === 'FACULTY' || UserStore.getUser.user_role === 'HOD' || UserStore.getUser.user_role === 'PRINCIPAL' ) && mode === "manage" && <Button disableElevation variant='contained' sx={{ textTransform: "capitalize" }} color='customThemeColor' onClick={() => navigate( '/faculty/material/add' )} startIcon={Icons.default.AddIcon}>Create</Button>}
            </Box>



            {!isLoading
                && filteredMaterials
                && filteredMaterials.length > 0
                && UserStore.getUser.user_role !== 'STUDENT'
                && <FacultyMaterialsList filteredMaterials={filteredMaterials} page={page} rowsPerPage={rowsPerPage} handleChangePage={handleChangePage} handleChangeRowsPerPage={handleChangeRowsPerPage} />}


            {!isLoading && <Box flexGrow={1} display="flex" flexDirection="column">
                {( !filteredMaterials || filteredMaterials.length === 0 ) && <Box display="flex" flexDirection="column" flexGrow={1}>
                    {mode === "manage" && <Box marginTop="10px" borderRadius={layout.borderRadius} padding="20px" flexGrow={1} bgcolor={palette.contentBg} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                        <img src={fileLogo} width="300px" style={{ marginBottom: "20px" }} alt="No Material!" />
                        < Typography textAlign="center" color="textSecondary" variant='subtitle2'>{( UserStore.getUser.user_role !== 'STUDENT' ) ? "You doesn't seem to have any materials!" : ( institute === "0" && searchText === "" ) ? "Please select an institute to browse study materials or search" : "Selected institute doesn't seem to have any study materials"}</Typography>
                        <Box marginTop="20px" display="flex" gap="20px" alignItems="center" flexWrap="wrap">
                            {materials && ( materials.length > 0 || searchText !== "" || institute !== "0" ) && <Button onClick={removeFilters} disableElevation variant='contained' color="secondary" sx={{ textTransform: "capitalize" }}>Remove filter</Button>}
                            {( UserStore.getUser.user_role !== 'STUDENT' ) && <Button color='secondary' variant='outlined' startIcon={Icons.default.AddIcon} onClick={() => navigate( "/faculty/material/add" )} sx={{ textTransform: "capitalize" }}>Add one</Button>}
                        </Box>
                    </Box>}
                </Box>}

            </Box>}
            {isLoading && <DataLoadingSpinner waitingMessage="Fetching materials..." />}
        </Box >
    )
} )

export default FacultyMaterials


/*

<Box
                position="relative"
                padding="20px"
                display="flex"
                gap="20px"
                alignItems="flex-start"
                flexDirection="column"
                justifyContent="space-between"
            >
                <Box height="100%" position="absolute" sx={{ zIndex: 0, inset: "0", background: `linear-gradient(46.37deg, #000000 -3.17%, rgba(0, 0, 0, 0) 150.03%)`, }}></Box>
                <Box position="relative" sx={{ zIndex: 10 }}>
                    <Typography color="white" variant="h5" width="fit-content" fontWeight="normal">{UserStore.getUser["user_role"] === "FACULTY" ? mode === "manage" ? "Your Study Materials" : "Study material audit" : "Study Materials"}</Typography>
                    <Typography color="whitesmoke" variant='subtitle2'>{UserStore.getUser["user_role"] === "STUDENT" ? "Browse study materials here" : mode === "manage" ? "Manage your study materials here" : `Audit all ${UserStore.getUser["user_role"] === "HOD" ? UserStore.getUser["user_dept_code"] : UserStore.getUser["user_org_code"]} study materials`}</Typography>
                </Box>

                {!isLoading && <Box width="100%" display="flex" gap="20px" justifyContent="space-between" flexWrap="wrap">

<Box display="flex" flexWrap="wrap" gap="20px" alignItems="stretch">
    {UserStore.getUser["user_role"] === "STUDENT" &&
        <Box flexShrink={0} flexGrow={1} display="flex">
            <Button disabled={isInstitutesLoading} sx={{ height: "100%", flexShrink: 0, flexGrow: 1, justifyContent: "space-between" }} variant='contained' disableElevation endIcon={isInstitutesLoading ? <CircularProgress size={14} /> : Icons.small.DropDown} onClick={e => setInstituteMenuAnchor( e.currentTarget )} color="customThemeColor">{isInstitutesLoading ? "Fetching institutes..." : institute !== "0" ? institute : "Select institute"}</Button>
            <Menu onClose={() => setInstituteMenuAnchor( null )} anchorEl={instituteMenuAnchor} open={Boolean( instituteMenuAnchor )} >
                <MenuItem onClick={() => handleStudentInstituteChange( "0" )} dense key={"all"} sx={{ fontSize: "14px", textTransform: "capitalize" }} value={"0"}>
                    All institutes
                </MenuItem>
                {OrganizationStore.getOrganization.map( org => {
                    return <MenuItem onClick={() => handleStudentInstituteChange( org.institute_name_short )} dense key={org.institute_id} sx={{ fontSize: "14px", textTransform: "capitalize" }} value={org.institute_name_short}>
                        {org.institute_name.toLowerCase()}
                    </MenuItem>
                } )}
            </Menu>
        </Box>}
    <CustomSearchBar style={{ flexGrow: 1, width: "400px", marginRight: "0" }} border='1px solid #d3d3d3' value={searchText} onChange={searchMaterials} placeholder={`Search by Subjects,${UserStore.getUser.user_role === 'STUDENT' ? " Faculty," : ""} Title or Subject code...`} />
</Box>

{ ( UserStore.getUser.user_role === 'FACULTY' || UserStore.getUser.user_role === 'HOD' || UserStore.getUser.user_role === 'PRINCIPAL' ) && mode === "manage" && <Button disableElevation variant='contained' sx={{ textTransform: "capitalize" }} color='customThemeColor' onClick={() => navigate( '/faculty/material/add' )} startIcon={Icons.default.AddIcon}>Create</Button> }
                </Box >}
            </Box >
            
    */